import { BLOG_CATEGORIES, getCategoryBySlug } from "@/lib/blog-categories";
import { CALCULATOR_CARDS } from "@/data/calculator-cards";
import { applyDocumentMeta, truncateMetaDescription, type DocumentMetaOptions } from "@/lib/document-meta";

const BLOG_CATEGORY_SLUGS = new Set(BLOG_CATEGORIES.map((c) => c.slug));

const NOINDEX_PREFIXES = [
  "/admin",
  "/login",
  "/profile",
  "/forgot-password",
  "/reset-password",
] as const;

export function pathnameIsNoindex(pathname: string): boolean {
  return NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

type StaticMeta = Omit<DocumentMetaOptions, "fullTitle"> & { fullTitle?: boolean };

const STATIC_ROUTES: Record<string, StaticMeta> = {
  "/": {
    title: "Matt Rebeiro | Strategic Advisor",
    description:
      "Matt Rebeiro is the #1 Advisor for Founders & CEOs, helping leaders from vision to strategy right through to execution.",
    fullTitle: true,
  },
  "/blog": {
    title: "Blog",
    description:
      "Practical articles on startup finance, SaaS metrics, market research, and founder strategy—written for founders and CEOs.",
  },
  "/podcast": {
    title: "Podcast",
    description: "Listen to conversations on growth, leadership, and execution for founders and CEOs.",
  },
  "/investor-directory": {
    title: "Investor Directory",
    description:
      "Browse investor firms, focus areas, and updates—curated for founders raising capital in software and SaaS.",
  },
  "/investors": {
    title: "Investor Directory",
    description:
      "Browse investor firms, focus areas, and updates—curated for founders raising capital in software and SaaS.",
  },
  "/accelerators": {
    title: "Accelerators Directory",
    description: "Discover startup accelerators and programs that support early-stage founders.",
  },
  "/cloud-credits": {
    title: "Cloud Credits Directory",
    description: "Find cloud provider credits and startup programs to reduce infrastructure costs.",
  },
  "/company-registrations": {
    title: "Company Registrations",
    description:
      "Official links to incorporate and register a business in Canada and the United States—federal, provincial, and state resources.",
  },
  "/trademark-registrations": {
    title: "Trademark Registrations",
    description:
      "Official trademark resources for Canada (CIPO) and the United States (USPTO)—search, file, and fee schedules.",
  },
  "/patent-registrations": {
    title: "Patent Registrations",
    description:
      "Official patent office resources for Canada and the United States—applications, search, and inventor support.",
  },
  "/book": {
    title: "Book",
    description: "Pre-order and learn about Matt Rebeiro’s guide for startup founders—vision through execution.",
  },
  "/calculators": {
    title: "Startup Calculators",
    description:
      "Free financial calculators for founders: runway, burn rate, LTV, CAC, TAM/SAM/SOM, valuation, cap table, and more.",
  },
  "/booking": {
    title: "Book a Growth Strategy Call",
    description: "Schedule a short strategy call—walk away with clear next moves for your software or SaaS company.",
  },
  "/saas-positioning-advisor": {
    // Standalone 58-char title—skip " | Matt Rebeiro" suffix to keep under Google's ~60-char display limit.
    title: "SaaS Positioning Advisor for Seed-to-Pre-Series-A Founders",
    description:
      "Help SaaS founders in Canada and the United States fix unclear positioning and low awareness so they win customers between Seed and Series A. Get the free Positioning Playbook.",
    fullTitle: true,
  },
  "/terms": {
    title: "Terms of Service",
    description: "Terms of service for mattrebeiro.com.",
  },
  "/privacy": {
    title: "Privacy Policy",
    description: "Privacy policy for mattrebeiro.com—how we collect, use, and protect your information.",
  },
  "/disclaimer": {
    title: "Disclaimer",
    description: "Legal disclaimer for mattrebeiro.com content, calculators, and advisory context.",
  },
  "/accessibility": {
    title: "Accessibility",
    description: "Accessibility statement and commitment for mattrebeiro.com.",
  },
  "/community": {
    title: "Community",
    description: "Community hub for founders—forum, events, and shared resources.",
  },
  "/community/forum": {
    title: "Community Forum",
    description: "Discuss strategy and execution with other founders and leaders in the community forum.",
  },
  "/community/calendar": {
    title: "Community Calendar",
    description: "Upcoming events, webinars, and meetups for the founder community.",
  },
  "/community/resources": {
    title: "Community Resources",
    description: "Templates, guides, and tools shared by the community.",
  },
  "/login": {
    title: "Sign in",
    description: "Sign in to your Matt Rebeiro account.",
  },
  "/admin": {
    title: "Admin",
    description: "Content and site administration.",
  },
  "/admin/users": {
    title: "User analytics",
    description: "Admin user analytics and management.",
  },
  "/profile": {
    title: "Profile",
    description: "Manage your account profile and settings.",
  },
  "/forgot-password": {
    title: "Forgot password",
    description: "Request a link to reset your password.",
  },
  "/reset-password": {
    title: "Reset password",
    description: "Set a new password for your account.",
  },
};

function metaForBlogSubpath(pathname: string): DocumentMetaOptions | null {
  if (!pathname.startsWith("/blog/")) return null;
  const slug = pathname.slice("/blog/".length);
  if (!slug || slug.includes("/")) return null;

  if (BLOG_CATEGORY_SLUGS.has(slug)) {
    const cat = getCategoryBySlug(slug);
    if (!cat) return null;
    return {
      title: `${cat.name} — Blog`,
      description: truncateMetaDescription(`${cat.description} Browse articles by Matt Rebeiro.`),
    };
  }

  return {
    title: "Blog",
    description:
      "Articles and guides for founders and CEOs on growth, finance, metrics, and strategy—by Matt Rebeiro.",
  };
}

function metaForCalculator(pathname: string): DocumentMetaOptions | null {
  if (!pathname.startsWith("/calculators/")) return null;
  const slug = pathname.slice("/calculators/".length);
  if (!slug || slug.includes("/")) return null;
  const calc = CALCULATOR_CARDS.find((c) => c.slug === slug);
  if (!calc) {
    return {
      title: "Calculator",
      description: "Startup financial calculator by Matt Rebeiro.",
    };
  }
  return {
    title: calc.title,
    description: calc.desc,
  };
}

/** Apply meta from pathname for static routes, blog branches, and calculators. */
export function syncDocumentMetaFromPath(pathname: string): void {
  const path = pathname.split("?")[0] || "/";
  const noindex = pathnameIsNoindex(path);

  const staticMeta = STATIC_ROUTES[path];
  if (staticMeta) {
    applyDocumentMeta({ ...staticMeta, noindex });
    return;
  }

  const calcMeta = metaForCalculator(path);
  if (calcMeta) {
    applyDocumentMeta({ ...calcMeta, noindex });
    return;
  }

  const blogMeta = metaForBlogSubpath(path);
  if (blogMeta) {
    applyDocumentMeta({ ...blogMeta, noindex });
    return;
  }

  applyDocumentMeta({
    title: "Page not found",
    description: "The page you requested could not be found.",
    noindex: true,
  });
}
